import { useEffect, useState } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Drawer, { FormField, FormTextarea, FormSelect, getSaveLabel } from "@/components/Drawer";
import { SaleVehicleSelect } from "@/components/SaleSelects";
import { SalePackagesPicker } from "@/components/PackagesSelector";
import BranchSelect from "@/components/BranchSelect";
import { fmtMoney } from "@/lib/format";
import { PAYMENT_METHODS } from "@/lib/constants";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY = {
  vehicle_id: "",
  customer_id: "",
  branch_id: "",
  gst_type: "intra",
  package_ids: [],
  package_item_exclusions: {},
  initial_deposit: "",
  initial_payment_method: "cash",
  initial_payment_reference: "",
  expected_delivery_date: "",
  expiry_date: "",
  notes: "",
};

function GstToggle({ value, onChange }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        GST Type
      </label>
      <div className="grid grid-cols-2 gap-2">
        {[
          { v: "intra", l: "Intra-state", n: "CGST + IGST" },
          { v: "inter", l: "Inter-state", n: "IGST only" },
        ].map((o) => {
          const active = value === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              data-testid={`booking-gst-${o.v}`}
              className={`px-3 py-2 border text-sm text-left transition-colors ${
                active ? "border-ink bg-ink text-white" : "border-canvas-line text-ink hover:border-ink"
              }`}
            >
              <div className="font-semibold">{o.l}</div>
              <div
                className={`font-mono text-[10px] uppercase tracking-wider ${
                  active ? "text-white/70" : "text-ink-subtle"
                }`}
              >
                {o.n}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function BookingFormDrawer({ open, customerId, onClose, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, customer_id: customerId || "" });
  const [vehicles, setVehicles] = useState([]);
  const [packageTotal, setPackageTotal] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, customer_id: customerId || "" });
    setPackageTotal(0);
    api
      .get("/vehicles", { params: { status: "available" } })
      .then((r) => setVehicles(r.data || []))
      .catch(() => setVehicles([]));
  }, [open, customerId]);

  const selectedVehicle = vehicles.find((v) => v.id === form.vehicle_id);

  const handleVehicle = (id) => {
    setForm((f) => ({ ...f, vehicle_id: id, package_ids: [], package_item_exclusions: {} }));
    setPackageTotal(0);
  };

  const handleTotalChange = (sum) => setPackageTotal(sum);

  const confirm = useConfirm();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await confirm({ title: "Save booking?", body: "This booking will be created. Confirm details before saving." }))) return;
    if (!form.vehicle_id || !form.customer_id) {
      toast.error("Vehicle and customer are required.");
      return;
    }
    const depositAmt = Number(form.initial_deposit) || 0;
    const depositRef = (form.initial_payment_reference || "").trim();
    if (
      depositAmt > 0 &&
      form.initial_payment_method !== "cash" &&
      !depositRef
    ) {
      toast.error(
        `${form.initial_payment_method.toUpperCase()} deposit needs a reference (UTR / txn id).`,
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        initial_deposit: depositAmt,
        initial_payment_reference: depositRef,
        package_item_exclusions: form.package_item_exclusions || {},
      };
      await api.post("/bookings", payload);
      toast.success("Booking created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="New Booking"
      title="Reserve a vehicle"
      testIdClose="close-booking-form"
    >
      <form onSubmit={handleSubmit} data-testid="booking-form" className="space-y-4">
        <SaleVehicleSelect
          vehicles={vehicles}
          value={form.vehicle_id}
          onChange={handleVehicle}
        />
        {selectedVehicle && (
          <GstToggle
            value={form.gst_type}
            onChange={(v) => setForm((f) => ({ ...f, gst_type: v }))}
          />
        )}
        {selectedVehicle && (
          <SalePackagesPicker
            vehicleId={selectedVehicle.id}
            availableIds={selectedVehicle.package_ids || []}
            selected={form.package_ids}
            exclusions={form.package_item_exclusions}
            gstType={form.gst_type}
            onChange={(package_ids, package_item_exclusions) =>
              setForm((f) => ({ ...f, package_ids, package_item_exclusions }))
            }
            onTotalChange={handleTotalChange}
          />
        )}
        <BranchSelect
          value={form.branch_id}
          onChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}
          testId="booking-branch"
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Expected Delivery"
            type="date"
            value={form.expected_delivery_date}
            onChange={(v) => setForm((f) => ({ ...f, expected_delivery_date: v }))}
            testId="booking-delivery-date"
          />
          <FormField
            label="Expiry / Hold Until"
            type="date"
            value={form.expiry_date}
            onChange={(v) => setForm((f) => ({ ...f, expiry_date: v }))}
            testId="booking-expiry-date"
          />
        </div>

        {packageTotal > 0 && (
          <div
            className="px-3 py-2 border border-canvas-line bg-canvas-panel font-mono text-[11px] text-ink"
            data-testid="booking-total-strip"
          >
            <div className="flex justify-between">
              <span className="text-ink-muted">Booking total</span>
              <span className="font-bold">{fmtMoney(packageTotal)}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Initial Deposit (₹)"
            type="number"
            value={form.initial_deposit}
            onChange={(v) => setForm((f) => ({ ...f, initial_deposit: v }))}
            testId="booking-deposit"
            placeholder="Token amount"
          />
          <FormSelect
            label="Deposit Method"
            value={form.initial_payment_method}
            onChange={(v) => setForm((f) => ({ ...f, initial_payment_method: v }))}
            options={PAYMENT_METHODS}
            testId="booking-deposit-method"
          />
        </div>

        {Number(form.initial_deposit) > 0 && (
          <FormField
            label={
              form.initial_payment_method === "cash"
                ? "Deposit Reference (optional)"
                : `Deposit Reference · ${form.initial_payment_method.toUpperCase()} (required)`
            }
            value={form.initial_payment_reference}
            onChange={(v) => setForm((f) => ({ ...f, initial_payment_reference: v }))}
            testId="booking-deposit-reference"
            placeholder={
              form.initial_payment_method === "cash"
                ? "Slip / receipt no."
                : "UTR / txn id / card slip"
            }
          />
        )}

        <FormTextarea
          label="Notes"
          value={form.notes}
          onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
          testId="booking-notes"
        />

        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="confirm-booking-button"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60"
          >
            {getSaveLabel(saving, false, "Saving…", "", "Create booking")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
